<script lang="ts" setup>
// @ts-ignore
import RouteStops from "@/views/trips/view/components/RouteStops.vue";
// @ts-ignore
import RouteInfo from "@/views/trips/view/components/RouteInfo.vue";
import type { TripDto, TripRouteDto } from "@/api/trips/dto/trip.dto";
import { computed, onMounted, ref } from "vue";
import { RouteStatus } from "@/data/trip";
import { useRoute, useRouter } from "vue-router";
import { useI18n } from "vue-i18n";
import { API } from "@/api/api";
import TruckRoutesDialog from "@/components/truck/TruckRoutesDialog.vue";
import { NEXT_ROUTES_WITH_EMPTY_MILES_IDS } from "@/data/query";

const props = defineProps<{
  trip: TripDto;
}>();

const emit = defineEmits(["change"]);

const api = new API();

const showTruckRoutesDialog = ref<boolean>(false);
const routesWithEmptyMiles = ref<TripRouteDto[]>([]);
const location = useRoute();
const router = useRouter();
const { t } = useI18n();

const nextRoutesWithEmptyMiles = computed(() => {
  const ids = location.query?.[NEXT_ROUTES_WITH_EMPTY_MILES_IDS] || [];

  return (Array.isArray(ids) ? ids : [ids]) as string[];
});

const truckIdsWithRoutesEmptyMiles = computed(
  () =>
    props.trip.routes
      .filter((route) => route.id && route.truckId && nextRoutesWithEmptyMiles.value.includes(String(route.id)))
      .map((route) => route.truckId) || [],
);

const emptyMilesRoutesTitle = computed(() => {
  if (!nextRoutesWithEmptyMiles.value?.length) {
    return "";
  }

  const routeList = nextRoutesWithEmptyMiles.value.map((id) => `#${id}`).join(", ");

  return `${t("general.route")} ${routeList} ${t("general.have").toLowerCase()} ${t("general.been-cancelled-or-changed").toLowerCase()}`;
});

async function getEmptyMilesRoutes() {
  if (!truckIdsWithRoutesEmptyMiles.value?.length) {
    clearNextRoutesWithEmptyMilesQuery();
    return;
  }

  try {
    const promises = truckIdsWithRoutesEmptyMiles.value
      .filter((id): id is number => id !== undefined)
      .map((truckId) =>
        api.trucks.getTruckRoutes(truckId, [RouteStatus.notStarted, RouteStatus.inProgress, RouteStatus.planned]),
      );

    const responses = await Promise.all(promises);

    const allRoutes = responses
      .filter((res) => res.success && res.data?.routes)
      .flatMap((res) => res.data?.routes) as TripRouteDto[];

    routesWithEmptyMiles.value = allRoutes;

    if (routesWithEmptyMiles.value.length) {
      showTruckRoutesDialog.value = true;
    } else {
      clearNextRoutesWithEmptyMilesQuery();
    }
  } catch (error) {
    console.error("Ошибка при загрузке маршрутов:", error);
  }
}

function onChange() {
  emit("change");
}

function clearNextRoutesWithEmptyMilesQuery() {
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const { [NEXT_ROUTES_WITH_EMPTY_MILES_IDS]: _, ...rest } = location.query;

  router.push({
    name: location.name,
    params: location.params,
    query: rest,
  });
}

onMounted(async () => {
  await getEmptyMilesRoutes();
});
</script>

<template>
  <v-row class="mt-4">
    <v-col v-for="route in props.trip.routes" :key="'info-' + route.id" lg="6" xl="4">
      <route-info :route="route" :trip-id="props.trip.id" @change="onChange"></route-info>
      <route-stops :route="route" :trip="props.trip" @change="onChange"></route-stops>
    </v-col>
  </v-row>

  <truck-routes-dialog
    v-model="showTruckRoutesDialog"
    :routes="routesWithEmptyMiles"
    @on-close="clearNextRoutesWithEmptyMilesQuery"
  >
    <template #header>
      <div class="text-center">
        <div class="font-size-20 font-weight-500 mb-4 mt-5">{{ emptyMilesRoutesTitle }}</div>

        <span class="text-grey-darken-1"
          >{{ t("general.it-may-affected-empty-miles-of-the-next-planned-route") }}, <br />
          <span>
            {{ t("general.please-recalculate-or-cancel") }}
          </span></span
        >
      </div>
    </template>
  </truck-routes-dialog>
</template>

<style lang="scss" scoped></style>
